import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Video, 
  CheckCircle2, 
  AlertCircle,
  PlayCircle,
  Plus,
  Mail,
  UserPlus,
  MessageSquare,
  Flame,
  AlertTriangle,
  Flag,
  Minus
} from 'lucide-react';
import { ScheduleEvent, ScheduleTask } from '@/services/api/scheduleApi';

interface TodaysScheduleProps {
  events: ScheduleEvent[];
  tasks: ScheduleTask[];
  upcomingCalls: any[];
  onEventClick?: (event: ScheduleEvent) => void;
  onTaskComplete?: (task: ScheduleTask) => void;
  onAddEvent?: () => void;
  onCallJoin?: (call: any) => void;
}

const TodaysSchedule: React.FC<TodaysScheduleProps> = ({
  events,
  tasks,
  upcomingCalls,
  onEventClick,
  onTaskComplete,
  onAddEvent,
  onCallJoin
}) => {
  const getEventIcon = (eventType: string) => {
    console.log('🎯 Getting icon for event type:', eventType);
    switch (eventType?.toLowerCase()) {
      case 'call': 
        return <Phone className="h-4 w-4" />;
      case 'meeting': 
        return <Video className="h-4 w-4" />;
      case 'email': 
        return <Mail className="h-4 w-4" />;
      case 'follow_up': 
        return <UserPlus className="h-4 w-4" />;
      case 'campaign': 
        return <MessageSquare className="h-4 w-4" />;
      case 'task': 
        return <CheckCircle2 className="h-4 w-4" />;
      case 'reminder': 
        return <AlertCircle className="h-4 w-4" />;
      case 'other':
        return <Calendar className="h-4 w-4" />;
      default: 
        console.log('🔄 Using default clock icon for type:', eventType);
        return <Clock className="h-4 w-4" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Flame className="h-4 w-4 text-red-500" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium': return <Flag className="h-4 w-4 text-yellow-500" />;
      case 'low': return <Minus className="h-4 w-4 text-green-500" />;
      default: return <Flag className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatDateTime = (date: Date) => {
    const today = new Date();
    const itemDate = new Date(date);
    
    // If it's today, show only time
    if (itemDate.toDateString() === today.toDateString()) {
      return itemDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // For other dates, show date and time
    return itemDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Combine and categorize all schedule items
  const allScheduleItems = [
    ...events.map(event => ({
      ...event,
      type: 'event' as const,
      time: new Date(event.startDate),
      duration: Math.round((new Date(event.endDate).getTime() - new Date(event.startDate).getTime()) / (1000 * 60))
    })),
    ...tasks.map(task => ({
      ...task,
      type: 'task' as const,
      time: task.dueDate ? new Date(task.dueDate) : new Date(),
      duration: task.estimatedDuration || 30
    })),
    ...upcomingCalls.map(call => ({
      ...call,
      type: 'call' as const,
      time: call.start_datetime ? new Date(call.start_datetime) : new Date(),
      duration: Math.round((new Date(call.end_datetime || call.start_datetime).getTime() - new Date(call.start_datetime).getTime()) / (1000 * 60)) || 30,
      title: call.title || `Call - ${call.description || 'Upcoming Call'}`,
      priority: call.priority || 'medium' as const,
      eventType: 'call' as const
    }))
  ].sort((a, b) => a.time.getTime() - b.time.getTime());

  // Separate today's items from upcoming items
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

  const todaysItems = allScheduleItems.filter(item => 
    item.time >= todayStart && item.time < todayEnd
  );

  const upcomingItems = allScheduleItems.filter(item => 
    item.time >= todayEnd
  ).slice(0, 5); // Limit to next 5 upcoming items

  const renderScheduleCard = (title: string, items: any[], emptyMessage: string, showAddButton: boolean = false) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <Calendar className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center text-muted-foreground py-6">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              // Determine the correct event type for icon selection
              let iconEventType = '';
              if (item.type === 'event') {
                iconEventType = (item as ScheduleEvent).eventType;
              } else if (item.type === 'call') {
                // Use the actual event_type from the call data
                iconEventType = (item as any).event_type || 'call';
              } else if (item.type === 'task') {
                iconEventType = 'task';
              }
              
              console.log('🔍 Item details:', { 
                type: item.type, 
                eventType: (item as ScheduleEvent).eventType,
                iconEventType,
                title: item.title 
              });

              return (
                <div 
                  key={`${item.type}-${item.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer ${
                    ((item.type === 'event' && (item as ScheduleEvent).status === 'completed') ||
                     (item.type === 'task' && (item as ScheduleTask).status === 'completed')) 
                    ? 'opacity-70' : ''
                  }`}
                  onClick={() => {
                    if (item.type === 'event' && onEventClick) {
                      onEventClick(item as ScheduleEvent);
                    } else if (item.type === 'call' && onCallJoin) {
                      onCallJoin(item);
                    }
                  }}
                >
                  <div className="flex-shrink-0">
                    {getEventIcon(iconEventType)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="mb-1">
                      <h4 className={`font-medium text-sm truncate ${
                        ((item.type === 'event' && (item as ScheduleEvent).status === 'completed') ||
                         (item.type === 'task' && (item as ScheduleTask).status === 'completed'))
                        ? 'line-through text-muted-foreground' : ''
                      }`}>
                        {item.title}
                      </h4>
                    </div>
                    
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(item.time)}
                      </span>
                      {item.duration && (
                        <span>{item.duration} min</span>
                      )}
                      <div className="flex items-center">
                        {getPriorityIcon(item.priority)}
                      </div>
                      {item.type === 'event' && (item as ScheduleEvent).location && (
                        <span className="truncate">📍 {(item as ScheduleEvent).location}</span>
                      )}
                      {item.type === 'call' && (item as any).location && (
                        <span className="truncate">📍 {(item as any).location}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Completion indicator for completed items */}
                  {((item.type === 'event' && (item as ScheduleEvent).status === 'completed') ||
                    (item.type === 'task' && (item as ScheduleTask).status === 'completed')) && (
                    <div className="flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        {renderScheduleCard(
          "Today's Schedule", 
          todaysItems, 
          "No events, tasks, or calls scheduled for today",
          true
        )}
      </div>
      <div>
        {renderScheduleCard(
          "Upcoming Schedule", 
          upcomingItems, 
          "No upcoming events, tasks, or calls"
        )}
      </div>
    </div>
  );
};

export default TodaysSchedule;
